import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Space, Table, Tooltip } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/Navbar/Navbar.tsx';
import { deleteCredential } from '../../fetchHooks/deleteCredential';
import type { CredentialType } from '../../fetchHooks/useMyPasswords.ts';
import type { UserType } from '../../fetchHooks/useUserProfile.ts';
import { CopyToClipboardButton } from '../../utils/utilFunctions.tsx';

export const SensitiveInfoField = ({ text }: { text: string }) => {
  const [show, setShow] = useState(false);
  const onClick = () => {
    !show && setTimeout(() => setShow(false), 3000);
    setShow((prev) => !prev);
  };
  return (
    <>
      <span style={{ cursor: 'pointer' }} onClick={onClick} onKeyDown={onClick}>
        {show ? text : '****************'}
      </span>
      <CopyToClipboardButton text={text} />
    </>
  );
};

export const ModuleAccessDenied = () => {
  const { t } = useTranslation();
  return (
    <div>
      <Navbar />
      <div className="app-container">
        <p>{t('passwords.noAccessToModule')}</p>
      </div>
    </div>
  );
};

interface MyPasswordsTableProps {
  passwords: CredentialType[];
  setShowEditMydayModal: (item: CredentialType) => void;
  refetch: () => void;
  isLoading: boolean;
}

export const MyPasswordsTable = ({ passwords, setShowEditMydayModal, refetch, isLoading }: MyPasswordsTableProps) => {
  const { t } = useTranslation();
  const columns = [
    {
      title: t('passwords.websiteService'),
      dataIndex: 'website',
      sorter: (a: CredentialType, b: CredentialType) => a.website.localeCompare(b.website),
      width: '30%',
      ellipsis: true,
      key: 'website',
      render: (website: string, item: CredentialType) => {
        return (
          <Button shape="round" size="small" onClick={() => setShowEditMydayModal?.(item)} className="edit-button">
            <p>{website || '-'}</p>
          </Button>
        );
      },
      responsive: ['xs', 'sm', 'lg'],
    },

    {
      title: t('passwords.username'),
      dataIndex: 'username',
      sorter: (a: CredentialType, b: CredentialType) => a.username.localeCompare(b.username),
      key: 'username',

      render: (username: string) => <p>{username ? <SensitiveInfoField text={username} /> : '-'}</p>,
      responsive: ['xs', 'sm', 'lg'],
    },

    {
      title: t('passwords.password'),
      dataIndex: 'password',
      sorter: (a: CredentialType, b: CredentialType) => a.password.localeCompare(b.password),
      key: 'password',
      render: (password: string) => <SensitiveInfoField text={password} />,
      responsive: ['xs', 'sm', 'lg'],
    },
    {
      title: t('passwords.comment'),
      dataIndex: 'comment',
      sorter: (a: CredentialType, b: CredentialType) => a.comment.localeCompare(b.comment),
      key: 'comment',
      render: (comment: string) => <p>{comment ? <SensitiveInfoField text={comment} /> : '-'}</p>,
      responsive: ['sm', 'lg'],
    },
    {
      title: t('passwords.addedOn'),
      dataIndex: 'createdAt',
      sorter: (a: CredentialType, b: CredentialType) => a.createdAt.localeCompare(b.createdAt),
      key: 'createdAt',
      render: (createdAt: string) => (
        <p>{createdAt ? <SensitiveInfoField text={new Date(createdAt).toISOString().slice(0, 10)} /> : '-'}</p>
      ),
      responsive: ['sm', 'lg'],
    },
    {
      title: t('passwords.sharedWith'),
      dataIndex: 'users',
      key: 'users',
      render: (users: UserType[]) => (
        <p>
          {users?.map?.((user) => (
            <div key={user.id}>
              {user.firstname} {user.lastname}
              <br />
            </div>
          )) || '-'}
        </p>
      ),
      responsive: ['sm', 'lg'],
    },
    {
      title: t('myday.action'),
      key: 'action',
      render: (_: string, item: CredentialType) => (
        <Space.Compact size="middle">
          <Tooltip title={t('passwords.edit')}>
            <Button
              shape="round"
              size="small"
              icon={<EditOutlined />}
              onClick={() => setShowEditMydayModal?.(item)}
              className="edit-button"
            />
          </Tooltip>
          <Tooltip title={t('passwords.deletePassword')}>
            <Popconfirm
              title={t('passwords.confirmDeletePassword')}
              onConfirm={() => deleteCredential(item.id, refetch)}
              onCancel={() => console.log('Canceled')}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              <Button className="edit-button" shape="round" size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space.Compact>
      ),
      responsive: ['sm', 'lg'],
    },
  ];

  return (
    <Table
      loading={isLoading}
      dataSource={passwords}
      columns={columns as any}
      rowKey="id"
      style={{ marginBottom: 16 }}
    />
  );
};
